'use client'

import styled from '@emotion/styled'
import { Box, Button, Container, Typography } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, Eye, Globe } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLanguage } from './language'
import { languages } from './translations'

import polarTensorLogo from '~/assets/polar_tensor_logo.png'
import cryptoBackground from '~/assets/webinar_crypto_bg.png'
import endScreenImage from '~/assets/webinar_end_screen.png'
import videoThumbnail from '~/assets/webinar_video_thumb.png'

const premiumEase = [0.16, 1, 0.3, 1] as const
const REACTION_EMOJIS = ['🔥', '🚀', '👏', '❤️', '😮']

interface FloatingEmoji {
  id: number
  emoji: string
  x: number
}

// ── Splash ──

const SplashOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: #0a1628;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SplashGlow = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 600px;
  height: 600px;
  background: rgba(6, 182, 212, 0.2);
  border-radius: 50%;
  filter: blur(120px);
`

const SplashContent = styled(motion.div)`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const SplashLogo = styled(motion.img)`
  height: 32px;
  width: auto;
  object-fit: contain;
  @media (min-width: 600px) {
    height: 48px;
  }
  @media (min-width: 900px) {
    height: 64px;
  }
  @media (min-width: 1200px) {
    height: 80px;
  }
`

const SplashDots = styled(motion.div)`
  margin-top: 24px;
  display: flex;
  gap: 4px;
`

// ── Root ──

const Root = styled(Box)`
  min-height: 100vh;
  background: #0a1628;
  color: white;
  font-family: sans-serif;
  position: relative;

  ::selection {
    background: rgba(6, 182, 212, 0.2);
    color: #22d3ee;
  }
`

// ── Background ──

const BgLayer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
`

const BgImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.2;
`

const BgGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, #0a1628, rgba(10, 22, 40, 0.8), #0a1628);
`

const BgGlow = styled.div<{ $position: 'top' | 'bottom' }>`
  position: absolute;
  width: 50%;
  height: 50%;
  background: ${(p) =>
    p.$position === 'top' ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.1)'};
  border-radius: 50%;
  filter: blur(120px);
  ${(p) =>
    p.$position === 'top' ? 'top: -10%; left: -10%;' : 'bottom: -10%; right: -10%;'}
`

// ── Header ──

const Header = styled.header`
  position: relative;
  z-index: 100;
  width: 100%;
  padding: 16px;
  @media (min-width: 600px) {
    padding: 24px;
  }
`

const HeaderInner = styled(Container)`
  max-width: 1200px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Logo = styled(motion.img)`
  height: 24px;
  width: auto;
  @media (min-width: 600px) {
    height: 32px;
  }
  @media (min-width: 900px) {
    height: 40px;
  }
`

// ── Language Switcher (same as info page) ──

const LangButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #1a1f2e;
  border: 1px solid rgba(107, 114, 128, 0.4);
  color: rgb(209, 213, 219);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(6, 182, 212, 0.5);
    color: white;
  }

  @media (min-width: 900px) {
    padding: 8px 12px;
    font-size: 0.875rem;
  }
`

const LangDropdown = styled.div`
  margin-top: 8px;
  width: 170px;
  max-height: 280px;
  overflow-y: auto;
  border-radius: 12px;
  background: #0d1a2d;
  border: 1px solid rgba(107, 114, 128, 0.3);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7);
  z-index: 200;

  @media (min-width: 600px) {
    width: 192px;
  }
`

const LangOption = styled.button<{ $active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  background: ${(p) => (p.$active ? 'rgba(6, 182, 212, 0.15)' : 'transparent')};
  color: ${(p) => (p.$active ? 'rgb(103, 232, 249)' : 'rgb(209, 213, 219)')};

  &:hover {
    background: ${(p) =>
      p.$active ? 'rgba(6, 182, 212, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
    color: ${(p) => (p.$active ? 'rgb(103, 232, 249)' : 'white')};
  }
`

const LangName = styled.span`
  display: none;
  @media (min-width: 600px) {
    display: inline;
  }
`

// ── Video Container ──

const VideoShimmer = styled.div`
  position: relative;
  padding: 4px;
  border-radius: 12px;
  max-width: 1024px;
  margin: 0 auto;
  background: linear-gradient(
    135deg,
    rgba(6, 182, 212, 0.8) 0%,
    rgba(34, 211, 238, 0.4) 25%,
    rgba(6, 182, 212, 0.2) 50%,
    rgba(34, 211, 238, 0.4) 75%,
    rgba(6, 182, 212, 0.8) 100%
  );

  @media (min-width: 900px) {
    padding: 6px;
    border-radius: 16px;
  }
`

const VideoShimmerGlow = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 12px;
  opacity: 0.5;
  filter: blur(16px);
  background: linear-gradient(
    135deg,
    rgba(6, 182, 212, 0.6) 0%,
    rgba(34, 211, 238, 0.3) 50%,
    rgba(6, 182, 212, 0.6) 100%
  );

  @media (min-width: 900px) {
    border-radius: 16px;
  }
`

const VideoInner = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #0d1117;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);

  @media (min-width: 900px) {
    border-radius: 12px;
  }
`

// ── Badges ──

const LiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 9999px;
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: #f87171;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;

  @media (min-width: 900px) {
    font-size: 0.875rem;
  }
`

const ViewerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;

  @media (min-width: 900px) {
    font-size: 0.875rem;
  }
`

const PingDot = styled.span`
  position: relative;
  display: flex;
  height: 10px;
  width: 10px;
`

const PingRing = styled.span`
  position: absolute;
  display: inline-flex;
  height: 100%;
  width: 100%;
  border-radius: 50%;
  opacity: 0.75;
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;

  @keyframes ping {
    75%,
    100% {
      transform: scale(2);
      opacity: 0;
    }
  }
`

const PingSolid = styled.span<{ $color?: string }>`
  position: relative;
  display: inline-flex;
  border-radius: 50%;
  height: 100%;
  width: 100%;
  background: ${(p) => p.$color || '#ef4444'};
`

// ── Overlay badges during playback ──

const OverlayBadgeRow = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: none;
`

const OverlayBadge = styled.span<{ $bg: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 6px;
  background: ${(p) => p.$bg};
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
`

const OverlayViewerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.75rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
`

const OverlayTimeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.75rem;
  font-family: monospace;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
`

// ── Join button ──

const JoinButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  border-radius: 16px;
  background: linear-gradient(to right, #ef4444, #dc2626);
  color: white;
  font-weight: 700;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  box-shadow: 0 25px 50px -12px rgba(239, 68, 68, 0.4);
  transition: all 0.3s;

  &:hover {
    background: linear-gradient(to right, #dc2626, #b91c1c);
    transform: scale(1.05);
  }

  @media (min-width: 600px) {
    padding: 20px 40px;
    font-size: 1.125rem;
  }

  @media (min-width: 900px) {
    padding: 24px 56px;
    font-size: 1.25rem;
  }
`

// ── Thumbnail overlay bar ──

const ThumbnailBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;

  @media (min-width: 600px) {
    padding: 16px;
  }
  @media (min-width: 900px) {
    padding: 24px;
  }
`

const ThumbnailBarInner = styled.div`
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (min-width: 600px) {
    padding: 12px 16px;
  }
  @media (min-width: 900px) {
    padding: 16px 24px;
  }
`

const DesktopLivePill = styled.span`
  display: none;

  @media (min-width: 600px) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 9999px;
    background: rgba(239, 68, 68, 0.2);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #f87171;
    font-size: 0.75rem;
    font-weight: 600;
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`

// ── Emoji reaction bar ──

const ReactionBar = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-top: none;
  border-radius: 0 0 12px 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (min-width: 600px) {
    gap: 16px;
  }
`

const ReactionLabel = styled.span`
  color: rgba(255, 255, 255, 0.4);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  flex-shrink: 0;

  @media (min-width: 600px) {
    font-size: 12px;
  }
`

const EmojiBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  transition: all 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
  &:active {
    transform: scale(0.9);
  }

  @media (min-width: 600px) {
    width: 40px;
    height: 40px;
    font-size: 1.125rem;
  }
  @media (min-width: 900px) {
    width: 44px;
    height: 44px;
    font-size: 1.25rem;
  }
`

// ── Floating emoji container ──

const EmojiOverlay = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  top: 0;
  width: 96px;
  z-index: 10;
  pointer-events: none;
  overflow: hidden;

  @media (min-width: 600px) {
    width: 128px;
  }
`

// ── Stats ──

const StatCard = styled.div`
  text-align: center;
  padding: 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(6, 182, 212, 0.2);

  @media (min-width: 900px) {
    padding: 16px;
  }
`

const StatValue = styled.p`
  color: #22d3ee;
  font-weight: 700;
  font-size: 1.25rem;
  margin: 0;

  @media (min-width: 600px) {
    font-size: 1.5rem;
  }
  @media (min-width: 900px) {
    font-size: 2rem;
  }
  @media (min-width: 1200px) {
    font-size: 2.25rem;
  }
`

const StatLabel = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.75rem;
  margin: 4px 0 0;

  @media (min-width: 900px) {
    font-size: 0.875rem;
  }
`

// ── CTA ──

const CTAButton = styled(Button)`
  height: 48px;
  padding: 0 32px;
  background: linear-gradient(to right, #06b6d4, #0891b2);
  color: white;
  font-weight: 700;
  font-size: 1rem;
  border-radius: 9999px;
  text-transform: none;
  box-shadow: 0 20px 25px -5px rgba(6, 182, 212, 0.3);
  transition: all 0.2s;

  &:hover {
    background: linear-gradient(to right, #0891b2, #0e7490);
    transform: scale(1.05);
  }

  @media (min-width: 900px) {
    height: 56px;
    padding: 0 40px;
    font-size: 1.125rem;
  }
  @media (min-width: 1200px) {
    height: 64px;
    padding: 0 48px;
    font-size: 1.25rem;
  }
`

// ── Footer ──

const Footer = styled.footer`
  position: relative;
  z-index: 10;
  width: 100%;
  padding: 24px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`

// ── Hooks ──

function useViewerCount() {
  const [count, setCount] = useState(1200)
  useEffect(() => {
    setCount(Math.floor(Math.random() * 300) + 1200)
    const interval = setInterval(() => {
      setCount((prev) => {
        const delta = Math.floor(Math.random() * 15) - 5
        return Math.max(800, Math.min(2000, prev + delta))
      })
    }, 4000)
    return () => clearInterval(interval)
  }, [])
  return count
}

function useVideoStartTime() {
  const [startSeconds, setStartSeconds] = useState(75)
  useEffect(() => {
    setStartSeconds(60 + Math.floor(Math.random() * 31))
  }, [])
  return startSeconds
}

// ── Component ──

export default function WebinarPage() {
  const { lang, setLang, t } = useLanguage()
  const pathname = usePathname()
  const username = pathname.split('/').filter(Boolean)[1] ?? null
  const signUpUrl = username ? `/sign/up/${username}` : '/sign/up'
  const [showLangMenu, setShowLangMenu] = useState(false)
  const langMenuRef = useRef<HTMLDivElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [showSplash, setShowSplash] = useState(true)
  const [splashPhase, setSplashPhase] = useState<'logo' | 'fadeOut' | 'done'>('logo')
  const viewerCount = useViewerCount()
  const videoStartTime = useVideoStartTime()
  const [floatingEmojis, setFloatingEmojis] = useState<FloatingEmoji[]>([])
  const emojiIdRef = useRef(0)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const joinedAtRef = useRef<number | null>(null)
  const [videoEnded, setVideoEnded] = useState(false)

  const currentLang = languages.find((l) => l.code === lang)!

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setShowLangMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isPlaying) return
    if (!joinedAtRef.current) {
      joinedAtRef.current = Date.now()
      setElapsedSeconds(videoStartTime)
    }
    const interval = setInterval(() => {
      const sinceJoin = Math.floor((Date.now() - joinedAtRef.current!) / 1000)
      setElapsedSeconds(videoStartTime + sinceJoin)
    }, 1000)
    return () => clearInterval(interval)
  }, [isPlaying, videoStartTime])

  const elapsedFormatted = useMemo(() => {
    const hrs = Math.floor(elapsedSeconds / 3600)
    const mins = Math.floor((elapsedSeconds % 3600) / 60)
    const secs = elapsedSeconds % 60
    const pad = (n: number) => n.toString().padStart(2, '0')
    if (hrs > 0) return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`
    return `${pad(mins)}:${pad(secs)}`
  }, [elapsedSeconds])

  const spawnEmoji = useCallback((emoji: string, fromUser = false) => {
    const id = emojiIdRef.current++
    const x = fromUser ? 50 + Math.random() * 40 : 10 + Math.random() * 80
    setFloatingEmojis((prev) => [...prev.slice(-30), { id, emoji, x }])
    setTimeout(() => {
      setFloatingEmojis((prev) => prev.filter((e) => e.id !== id))
    }, 2500)
  }, [])

  useEffect(() => {
    if (!isPlaying) return
    let cancelled = false
    const scheduleNext = () => {
      if (cancelled) return
      const delay = 1200 + Math.random() * 2500
      setTimeout(() => {
        if (cancelled) return
        const count = Math.random() < 0.3 ? 2 : 1
        for (let i = 0; i < count; i++) {
          const emoji =
            REACTION_EMOJIS[Math.floor(Math.random() * REACTION_EMOJIS.length)]
          spawnEmoji(emoji, false)
        }
        scheduleNext()
      }, delay)
    }
    scheduleNext()
    return () => {
      cancelled = true
    }
  }, [isPlaying, spawnEmoji])

  // Splash animation
  useEffect(() => {
    const logoTimer = setTimeout(() => setSplashPhase('fadeOut'), 2000)
    const hideTimer = setTimeout(() => {
      setSplashPhase('done')
      setShowSplash(false)
    }, 2800)
    return () => {
      clearTimeout(logoTimer)
      clearTimeout(hideTimer)
    }
  }, [])

  const videoSrc = useMemo(
    () => `https://s3.polar-tensor.io/polar/vids/${encodeURIComponent(t.videoFile)}`,
    [t.videoFile],
  )

  return (
    <Root>
      {/* Splash Screen */}
      <AnimatePresence>
        {showSplash && (
          <SplashOverlay
            initial={{ opacity: 1 }}
            animate={{ opacity: splashPhase === 'fadeOut' ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.8,
              ease: premiumEase as unknown as number[],
            }}
            style={{
              pointerEvents: splashPhase === 'fadeOut' ? 'none' : 'auto',
            }}
          >
            <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
              <SplashGlow
                initial={{ scale: 0, x: '-50%', y: '-50%' }}
                animate={{ scale: 1, x: '-50%', y: '-50%' }}
                transition={{
                  duration: 1.5,
                  ease: premiumEase as unknown as number[],
                }}
              />
            </Box>
            <SplashContent
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{
                opacity: splashPhase === 'fadeOut' ? 0 : 1,
                scale: splashPhase === 'fadeOut' ? 1.1 : 1,
                y: splashPhase === 'fadeOut' ? -30 : 0,
              }}
              transition={{
                duration: 0.8,
                ease: premiumEase as unknown as number[],
              }}
            >
              <SplashLogo
                src={polarTensorLogo.src}
                alt='Polar Tensor'
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.3,
                  ease: premiumEase as unknown as number[],
                }}
              />
              <SplashDots
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
              >
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#22d3ee',
                    }}
                    animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: i * 0.2,
                    }}
                  />
                ))}
              </SplashDots>
            </SplashContent>
          </SplashOverlay>
        )}
      </AnimatePresence>

      {/* Background */}
      <BgLayer>
        <BgImg src={cryptoBackground.src} alt='' />
        <BgGradient />
        <BgGlow $position='top' />
        <BgGlow $position='bottom' />
      </BgLayer>

      {/* Header */}
      <Header style={{ opacity: showSplash ? 0 : 1, transition: 'opacity 0.4s' }}>
        <HeaderInner>
          <Logo
            src={polarTensorLogo.src}
            alt='Polar Tensor'
            initial={{ opacity: 0, y: -10 }}
            animate={!showSplash ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
            transition={{
              duration: 0.6,
              ease: premiumEase as unknown as number[],
            }}
          />
          <Box sx={{ position: 'relative' }} ref={langMenuRef}>
            <LangButton onClick={() => setShowLangMenu(!showLangMenu)}>
              <Globe size={14} />
              <span>{currentLang.flag}</span>
              <LangName>{currentLang.name}</LangName>
              <ChevronDown
                size={12}
                style={{
                  transition: 'transform 0.2s',
                  transform: showLangMenu ? 'rotate(180deg)' : 'none',
                }}
              />
            </LangButton>
            <AnimatePresence>
              {showLangMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -5, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -5, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: '100%',
                  }}
                >
                  <LangDropdown>
                    {languages.map((language) => (
                      <LangOption
                        key={language.code}
                        $active={lang === language.code}
                        onClick={() => {
                          setLang(language.code)
                          setShowLangMenu(false)
                        }}
                      >
                        <span>{language.flag}</span>
                        <span>{language.name}</span>
                      </LangOption>
                    ))}
                  </LangDropdown>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </HeaderInner>
      </Header>

      {/* Main Content */}
      <Box
        component='main'
        sx={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          px: 2,
          pb: 8,
          opacity: showSplash ? 0 : 1,
          transition: 'opacity 0.4s',
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          {/* Hero Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={!showSplash ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{
              duration: 0.8,
              ease: premiumEase as unknown as number[],
            }}
            style={{ textAlign: 'center', marginBottom: 32 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                mb: 2,
              }}
            >
              <LiveBadge>
                <PingDot>
                  <PingRing style={{ background: '#f87171' }} />
                  <PingSolid />
                </PingDot>
                {t.liveNow}
              </LiveBadge>
              <ViewerBadge>
                <Eye style={{ width: 14, height: 14 }} />
                <span>{viewerCount.toLocaleString()}</span> {t.watching}
              </ViewerBadge>
            </Box>

            <Typography
              component='h1'
              sx={{
                fontSize: {
                  xs: '1.5rem',
                  sm: '1.875rem',
                  md: '2.25rem',
                  lg: '3rem',
                  xl: '3.75rem',
                },
                fontWeight: 700,
                letterSpacing: '-0.025em',
                lineHeight: 1.15,
                px: 1,
              }}
            >
              <Box
                component='span'
                sx={{
                  background: 'linear-gradient(to right, #22d3ee, #06b6d4)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Polar Tensor
              </Box>{' '}
              {t.liveWebinar}
            </Typography>

            <Typography
              sx={{
                color: '#9ca3af',
                fontSize: { xs: '0.875rem', md: '1.125rem', lg: '1.25rem' },
                maxWidth: 768,
                mx: 'auto',
                mt: 2,
                px: 2,
              }}
            >
              {t.description}
            </Typography>
          </motion.div>

          {/* Video Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={!showSplash ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <VideoShimmer>
              <VideoShimmerGlow />
              <VideoInner>
                {videoEnded ? (
                  /* End Screen */
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#0d1117',
                    }}
                  >
                    <img
                      src={endScreenImage.src}
                      alt='Polar Tensor'
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(0,0,0,0.3)',
                      }}
                    />
                    <motion.div
                      style={{
                        position: 'relative',
                        zIndex: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 16,
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.6,
                        ease: premiumEase as unknown as number[],
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'white',
                          fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                          fontWeight: 500,
                          textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                        }}
                      >
                        {t.webinarEnded}
                      </Typography>
                      <CTAButton href={signUpUrl}>{t.signUpNow}</CTAButton>
                    </motion.div>
                  </Box>
                ) : isPlaying ? (
                  /* Playing State */
                  <>
                    <video
                      src={videoSrc}
                      autoPlay
                      onEnded={() => setVideoEnded(true)}
                      playsInline
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 5,
                      }}
                    />
                    <OverlayBadgeRow>
                      <OverlayBadge $bg='rgba(220, 38, 38, 0.9)'>
                        <PingDot style={{ width: 8, height: 8 }}>
                          <PingRing style={{ background: 'white' }} />
                          <PingSolid $color='white' />
                        </PingDot>
                        LIVE
                      </OverlayBadge>
                      <OverlayViewerBadge>
                        <Eye style={{ width: 12, height: 12 }} />
                        {viewerCount.toLocaleString()}
                      </OverlayViewerBadge>
                      <OverlayTimeBadge>{elapsedFormatted}</OverlayTimeBadge>
                    </OverlayBadgeRow>
                    <EmojiOverlay>
                      <AnimatePresence>
                        {floatingEmojis.map((fe) => (
                          <motion.div
                            key={fe.id}
                            style={{
                              position: 'absolute',
                              fontSize: '1.5rem',
                              left: `${fe.x % 100}%`,
                              bottom: 0,
                            }}
                            initial={{
                              opacity: 1,
                              y: 0,
                              scale: 0.5,
                            }}
                            animate={{
                              opacity: 0,
                              y: -300,
                              scale: 1.2,
                            }}
                            exit={{ opacity: 0 }}
                            transition={{
                              duration: 2.5,
                              ease: 'easeOut',
                            }}
                          >
                            {fe.emoji}
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </EmojiOverlay>
                  </>
                ) : (
                  /* Thumbnail State */
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={videoThumbnail.src}
                      alt='Live webinar thumbnail'
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(0,0,0,0.5)',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        zIndex: 20,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <OverlayBadge $bg='#dc2626'>
                        <PingDot style={{ width: 8, height: 8 }}>
                          <PingRing style={{ background: 'white' }} />
                          <PingSolid $color='white' />
                        </PingDot>
                        LIVE
                      </OverlayBadge>
                      <OverlayViewerBadge>
                        <Eye style={{ width: 12, height: 12 }} />
                        {viewerCount.toLocaleString()}
                      </OverlayViewerBadge>
                    </Box>
                    <Box
                      sx={{
                        position: 'relative',
                        zIndex: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <JoinButton onClick={() => setIsPlaying(true)}>
                        <PingDot style={{ width: 12, height: 12 }}>
                          <PingRing style={{ background: 'white' }} />
                          <PingSolid $color='white' />
                        </PingDot>
                        {t.joinLiveNow}
                      </JoinButton>
                      <Typography
                        sx={{
                          color: 'rgba(255,255,255,0.7)',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          fontWeight: 500,
                          animation:
                            'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                          },
                        }}
                      >
                        {t.webinarInProgress}
                      </Typography>
                    </Box>
                    <ThumbnailBar>
                      <ThumbnailBarInner>
                        <Box>
                          <Typography
                            sx={{
                              color: 'white',
                              fontWeight: 600,
                              fontSize: {
                                xs: '0.875rem',
                                sm: '1rem',
                                md: '1.125rem',
                              },
                            }}
                          >
                            {t.polarTensorLiveWebinar}
                          </Typography>
                          <Typography
                            sx={{
                              color: 'rgba(255,255,255,0.6)',
                              fontSize: {
                                xs: '0.75rem',
                                sm: '0.875rem',
                                md: '1rem',
                              },
                            }}
                          >
                            {t.happeningNow} — {viewerCount.toLocaleString()}{' '}
                            {t.peopleWatching}
                          </Typography>
                        </Box>
                        <DesktopLivePill>
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: '#f87171',
                              display: 'inline-block',
                            }}
                          />
                          LIVE
                        </DesktopLivePill>
                      </ThumbnailBarInner>
                    </ThumbnailBar>
                  </Box>
                )}
              </VideoInner>
            </VideoShimmer>

            {/* Emoji Reaction Bar */}
            <AnimatePresence>
              {isPlaying && !videoEnded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{
                    duration: 0.4,
                    ease: premiumEase as unknown as number[],
                  }}
                  style={{
                    maxWidth: 1024,
                    margin: '0 auto',
                    overflow: 'hidden',
                    width: 'fit-content',
                  }}
                >
                  <ReactionBar>
                    <ReactionLabel>{t.react}</ReactionLabel>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                      }}
                    >
                      {REACTION_EMOJIS.map((emoji) => (
                        <EmojiBtn
                          key={emoji}
                          onClick={() => spawnEmoji(emoji, true)}
                        >
                          {emoji}
                        </EmojiBtn>
                      ))}
                    </Box>
                  </ReactionBar>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Stats + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={!showSplash ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{ textAlign: 'center', marginTop: 40 }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: { xs: 1.5, md: 3 },
                maxWidth: 768,
                mx: 'auto',
                mb: { xs: 4, md: 5 },
              }}
            >
              <StatCard>
                <StatValue>22.04%</StatValue>
                <StatLabel>{t.avgProfit}</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>2018</StatValue>
                <StatLabel>{t.startedIn}</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>10.000+</StatValue>
                <StatLabel>{t.clients}</StatLabel>
              </StatCard>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CTAButton href={signUpUrl}>{t.getStarted}</CTAButton>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                }}
              >
                {t.joinThousands}
              </Typography>
            </Box>
          </motion.div>
        </Box>
      </Box>

      {/* Footer */}
      <Footer style={{ opacity: showSplash ? 0 : 1, transition: 'opacity 0.4s' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', textAlign: 'center' }}>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.3)',
              fontSize: { xs: '0.75rem', md: '0.875rem' },
            }}
          >
            {t.copyright}
          </Typography>
        </Box>
      </Footer>
    </Root>
  )
}
